import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  CircularProgress,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { v4 as uuidv4 } from 'uuid';
import { useCreateProgramLevel, useUpdateProgramLevel } from '../../../api/hooks/useProgramLevels';
import { useOrganization } from '../../../contexts/OrganizationContext';
import { ProgramLevelDto } from '../../../api/types/dtos/program';

interface CreateLevelModalProps {
  open: boolean;
  onClose: () => void;
  programId: string;
  editingLevel?: ProgramLevelDto | null;
}

const CreateLevelModal: React.FC<CreateLevelModalProps> = ({
  open,
  onClose,
  programId,
  editingLevel = null,
}) => {
  const [id, setId] = useState<string | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  
  const { selectedOrganization } = useOrganization();
  const { enqueueSnackbar } = useSnackbar();
  const createMutation = useCreateProgramLevel(
    selectedOrganization?.id || '',
    programId
  );
  const updateMutation = useUpdateProgramLevel(
    selectedOrganization?.id || '',
    programId
  );

  useEffect(() => {
    if (editingLevel && open) {
      setId(editingLevel.id || null);
      setName(editingLevel.name);
      setDescription(editingLevel.description || '');
    } else if (open) {
      setId(uuidv4()); // Generate UUID for new level
      setName('');
      setDescription('');
    }
  }, [editingLevel, open]);

  const handleSubmit = async () => {
    if (!name.trim()) {
      enqueueSnackbar('El nombre es requerido', { variant: 'error' });
      return;
    }

    try {
      if (editingLevel?.id) {
        await updateMutation.mutateAsync({
          id: editingLevel.id,
          data: {
            name: name.trim(),
            description: description.trim() || "",
            programId,
          },
        });
        enqueueSnackbar('Nivel actualizado exitosamente', { variant: 'success' });
      } else {
        await createMutation.mutateAsync({
          ...(id && { id }), // Include UUID in DTO
          name: name.trim(),
          description: description.trim() || "",
          programId,
          isActive: true,
        });
        enqueueSnackbar('Nivel creado exitosamente', { variant: 'success' });
      }

      handleClose();
    } catch (error) {
      enqueueSnackbar(`Error al ${editingLevel ? 'actualizar' : 'crear'} el nivel`, { variant: 'error' });
      console.error(`Error ${editingLevel ? 'updating' : 'creating'} level:`, error);
    }
  };

  const handleClose = () => {
    setId(null); // Clean UUID on close
    setName('');
    setDescription('');
    onClose();
  };

  const isEditing = !!editingLevel;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEditing ? 'Editar Nivel' : 'Crear Nuevo Nivel'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
          <TextField
            label="Nombre"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
          />
          <TextField
            label="Descripción"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            rows={3}
            fullWidth
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createMutation.isPending || updateMutation.isPending}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={createMutation.isPending || updateMutation.isPending}
          startIcon={(createMutation.isPending || updateMutation.isPending) && <CircularProgress size={20} />}
        >
          {isEditing ? 'Guardar' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateLevelModal;

